import React, { useContext, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import useTurnstile from "../hooks/useTurnstile";
import { AuthContext } from "../context/AuthContext";
import { CORES } from "../utils/participante";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const { containerRef: turnstileRef, token: turnstileToken, reset: resetTurnstile } =
    useTurnstile(process.env.REACT_APP_TURNSTILE_SITE_KEY);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
        turnstileToken,
      });

      login({ role: response.data.role, distrital_id: response.data.distrital_id });
      navigate("/listagem");
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao fazer login. Verifique suas credenciais.");
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
        backgroundColor: CORES.fundo,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: CORES.textoSuave, textTransform: "uppercase" }}
          >
            Área da equipe
          </Typography>
          <Typography component="h1" sx={{ fontSize: 25, fontWeight: 800, color: CORES.navy, lineHeight: 1.2 }}>
            Acesse o painel
          </Typography>
          <Typography sx={{ fontSize: 13, color: CORES.textoSuave, mt: 0.5 }}>
            Gestão de convidados e check-in do evento
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${CORES.borda}`,
            borderRadius: "10px",
            p: { xs: 2.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="E-mail"
            type="email"
            autoComplete="email"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            label="Senha"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            fullWidth
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box ref={turnstileRef} sx={{ display: "flex", justifyContent: "center" }} />

          <Button variant="contained" type="submit" fullWidth size="large" disabled={loading || !turnstileToken}>
            {loading ? <CircularProgress size={22} color="inherit" /> : "Entrar"}
          </Button>
        </Box>

        <Typography sx={{ textAlign: "center", fontSize: 11.5, color: "#8a97a6", mt: 2.5 }}>
          Associação Comercial de São Paulo
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
